#!/usr/bin/env node
/**
 * rename-rikumo.js
 *
 * Safely replaces the brand name "Rikumo" (capital R) with "rikumo"
 * across Shopify Dawn theme files.
 *
 * USAGE:
 *   node rename-rikumo.js              # DRY RUN — shows what would change, writes nothing
 *   node rename-rikumo.js --apply      # actually writes the changes
 *   node rename-rikumo.js --apply --all-caps   # also lowercase "RIKUMO"
 *
 * Run this from the ROOT of a pulled theme copy (e.g. ./dawn).
 * Always review `git diff` afterward and push to an UNPUBLISHED theme to test.
 */

const fs = require("fs");
const path = require("path");

// ---- config ---------------------------------------------------------------

const ROOT = process.cwd();
const APPLY = process.argv.includes("--apply");
const ALL_CAPS = process.argv.includes("--all-caps");

const EXTENSIONS = new Set([".liquid", ".json", ".js", ".css", ".md"]);
const SKIP_DIRS = new Set([".git", "node_modules"]);

// Whole-word, case-SENSITIVE match. \b avoids touching things like
// "data-rikumotheme" or substrings inside unrelated words.
const PATTERNS = [{ from: /\bRikumo\b/g, to: "rikumo" }];
if (ALL_CAPS) PATTERNS.push({ from: /\bRIKUMO\b/g, to: "rikumo" });

// Liquid logic that compares against the literal string. Lowercasing one
// side of these can silently break an `if` condition, so we only WARN.
const LOGIC_RE = /(==|!=|contains)\s*['"]Rikumo['"]|['"]Rikumo['"]\s*(==|!=)/;

// ---- helpers ---------------------------------------------------------------

function walk(dir, files = []) {
  for (const entry of fs.readdirSync(dir, { withFileTypes: true })) {
    if (entry.isDirectory()) {
      if (!SKIP_DIRS.has(entry.name)) walk(path.join(dir, entry.name), files);
    } else if (EXTENSIONS.has(path.extname(entry.name))) {
      files.push(path.join(dir, entry.name));
    }
  }
  return files;
}

function countMatches(text) {
  let n = 0;
  for (const { from } of PATTERNS) {
    const m = text.match(from);
    if (m) n += m.length;
  }
  return n;
}

function replaceAll(text) {
  let out = text;
  for (const { from, to } of PATTERNS) out = out.replace(from, to);
  return out;
}

// ---- main ------------------------------------------------------------------

const files = walk(ROOT);
let totalReplacements = 0;
const changed = [];
const logicWarnings = [];

for (const file of files) {
  const original = fs.readFileSync(file, "utf8");
  const hits = countMatches(original);
  if (hits === 0) continue;

  const rel = path.relative(ROOT, file);

  // Flag any Liquid comparison lines for manual review.
  original.split("\n").forEach((line, i) => {
    if (LOGIC_RE.test(line)) {
      logicWarnings.push(`${rel}:${i + 1}  ${line.trim()}`);
    }
  });

  totalReplacements += hits;
  changed.push({ rel, hits });

  if (APPLY) fs.writeFileSync(file, replaceAll(original), "utf8");
}

// ---- report ----------------------------------------------------------------

console.log(`\nScanned ${files.length} files under ${ROOT}`);
console.log(APPLY ? "MODE: APPLY (files written)\n" : "MODE: DRY RUN (no files changed)\n");

if (changed.length === 0) {
  console.log('No occurrences of capital-R "Rikumo" found.');
} else {
  console.log(`Found ${totalReplacements} occurrence(s) in ${changed.length} file(s):`);
  for (const { rel, hits } of changed) console.log(`  ${hits.toString().padStart(4)}  ${rel}`);
}

if (logicWarnings.length) {
  console.log("\n⚠️  MANUAL REVIEW — Liquid logic compares against 'Rikumo'.");
  console.log("   Lowercasing one side may break these conditions:");
  for (const w of logicWarnings) console.log(`   ${w}`);
}

if (!APPLY && changed.length) {
  console.log("\nRe-run with --apply to write these changes.");
}